import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Stack, Skeleton, Typography } from "@mui/material";
import MoodOutlinedIcon from "@mui/icons-material/MoodOutlined";
import { useGetSentimentSummaryQuery } from "@store/sessionApi";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import WeeklySentimentBar from "./WeeklySentimentBar";
import { Sentiment } from "@models/sentiment";

export interface CaregiverSentimentPanelProps {
  patientId: string;
}

const CaregiverSentimentPanel: FC<CaregiverSentimentPanelProps> = ({
  patientId,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetSentimentSummaryQuery(
    { patientId },
    { skip: !patientId },
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <MoodOutlinedIcon color="primary" />
          <Typography variant="h6">
            {t("sentiment.caregiverPanel.title")}
          </Typography>
        </Stack>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </Box>
    );
  }

  if (isError || !data) {
    return null;
  }

  const { lastActiveAt, last7Days, distribution, alerts } = data;
  const totalSessions =
    distribution.positive +
    distribution.neutral +
    distribution.negative +
    distribution.mixed;

  return (
    <DashboardTile title={t("sentiment.caregiverPanel.title")}>
      {lastActiveAt && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("sentiment.caregiverPanel.lastActive", {
            date: new Date(lastActiveAt).toLocaleDateString(),
          })}
        </Typography>
      )}

      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 0.5 }}
        >
          {t("sentiment.caregiverPanel.last7Days")}
        </Typography>
        <WeeklySentimentBar days={last7Days} />
      </Box>

      {totalSessions > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
          {(
            [
              Sentiment.Positive,
              Sentiment.Neutral,
              Sentiment.Mixed,
              Sentiment.Negative,
            ] as const
          ).map((sentiment) => {
            const count =
              distribution[sentiment.toLowerCase() as keyof typeof distribution];
            if (!count) {
              return null;
            }
            return (
              <Chip
                key={sentiment}
                size="small"
                variant="outlined"
                label={t("sentiment.caregiverPanel.distribution", {
                  sentiment: t(`sentiment.values.${sentiment}`),
                  count,
                })}
              />
            );
          })}
        </Stack>
      )}

      {totalSessions === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("sentiment.caregiverPanel.noData")}
        </Typography>
      )}

      {alerts && alerts.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {alerts.map((alert, index) => (
            <Typography key={index} variant="caption" color="warning.main">
              {t(`sentiment.caregiverPanel.alerts.${alert.type}`, {
                defaultValue: alert.label,
              })}
            </Typography>
          ))}
        </Stack>
      )}
    </DashboardTile>
  );
};

export default CaregiverSentimentPanel;
